/**
 * The current company's entitlements (org_entitlements()): plan, limits,
 * features, usage, and whether the server enforces them yet.
 *
 * Gated like the rest of billing (isBillingVisible()): while the plans are
 * drafts, production makes no query and every gate stays open.
 */
import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { isBillingVisible } from "@/lib/billing/gate";
import { gatesActive, type Entitlements, type MyEntitlements } from "@/lib/billing/entitlements";
import { useCurrentUser } from "@/hooks/useCurrentUser";

// org_entitlements() is not in the generated types until the migration is
// applied and the types are regenerated.
const db = supabase as unknown as SupabaseClient<any>;

// Until the plan enforcement migration is applied, the functions don't exist
// (PostgREST PGRST202): no retries then, and every gate stays open.
const retryUnlessMissing = (count: number, error: unknown) =>
  (error as { code?: string } | null)?.code !== "PGRST202" && count < 2;

export const entitlementsQueryKey = (orgId: string | undefined) =>
  ["billing", "entitlements", orgId ?? ""] as const;

export function useEntitlements() {
  const visible = isBillingVisible();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  const query = useQuery({
    queryKey: entitlementsQueryKey(orgId),
    enabled: visible && !!orgId,
    staleTime: 60_000,
    retry: retryUnlessMissing,
    queryFn: async (): Promise<Entitlements | null> => {
      const { data, error } = await db.rpc("org_entitlements", { org_id: orgId });
      if (error) throw error;
      return (data as Entitlements | null) ?? null;
    },
  });

  const entitlements = query.data ?? null;
  return {
    ...query,
    entitlements,
    /** True only when billing is visible AND the server enforces: then the UI gates. */
    active: gatesActive(entitlements, visible),
  };
}

/**
 * The person's own entitlements (my_entitlements()): the union over the
 * companies they own, for what belongs to the person rather than a company
 * (private mode). Gated like useEntitlements().
 */
export function useMyEntitlements() {
  const visible = isBillingVisible();
  const { data: user } = useCurrentUser();

  const query = useQuery({
    queryKey: ["billing", "my-entitlements", user?.id ?? ""],
    enabled: visible && !!user?.id,
    staleTime: 60_000,
    retry: retryUnlessMissing,
    queryFn: async (): Promise<MyEntitlements | null> => {
      const { data, error } = await db.rpc("my_entitlements");
      if (error) throw error;
      return (data as MyEntitlements | null) ?? null;
    },
  });

  const mine = query.data ?? null;
  return { ...query, mine, active: visible && !!mine?.enforced };
}
